/**
 * Anti-Hallucination Engine - Main Orchestrator
 * 
 * This is the unified interface that combines all anti-hallucination features:
 * - Semantic Verification
 * - Hallucination Fingerprinting
 * - Confidence Scoring
 * - Pattern Learning
 * - Context Optimization
 * 
 * WHAT MAKES THIS BEST IN THE WORLD:
 * 1. Multi-layer validation - catches hallucinations at multiple levels
 * 2. Learning system - gets better with every accept/reject
 * 3. Confidence-based gating - prevents low-quality suggestions
 * 4. Smart context - maximizes relevant code in prompts
 * 5. Real-time feedback loop - adapts to your codebase
 */

import * as fs from 'fs';
import * as path from 'path';
import { SemanticVerifier } from './SemanticVerifier';
import { HallucinationDetector, DetectionResult } from './HallucinationDetector';
import { ConfidenceScorer, ConfidenceScore } from './ConfidenceScorer';
import { PatternLearner, LearningStats } from './PatternLearner';
import { ContextOptimizer, ContextRequest } from './ContextOptimizer';
import { VibecheckVerifier, ContextSource, VerificationBadge } from './VibecheckVerifier';
import {
  CodeSuggestion,
  VerificationResult,
  OptimizedContext,
  CodebaseKnowledge,
  LearningFeedback,
  ContextChunk,
} from './types';

export interface AnalysisResult {
  suggestion: CodeSuggestion;
  verification: VerificationResult;
  detection: DetectionResult;
  confidence: ConfidenceScore;
  patternScore: { score: number; matchedPatterns: any[] };
  shouldAccept: boolean;
  shouldReview: boolean;
  shouldReject: boolean;
  summary: AnalysisSummary;
}

export interface AnalysisSummary {
  overallScore: number;
  grade: string;
  recommendation: 'accept' | 'review' | 'reject';
  topIssues: string[];
  topStrengths: string[];
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
}

export interface EngineStats {
  totalAnalyzed: number;
  accepted: number;
  rejected: number;
  reviewed: number;
  averageConfidence: number;
  hallucinationsDetected: number;
  patternsLearned: number;
  accuracyImprovement: number;
}

export class AntiHallucinationEngine {
  private workspaceRoot: string;
  private verifier: SemanticVerifier;
  private detector: HallucinationDetector;
  private scorer: ConfidenceScorer;
  private learner: PatternLearner;
  private optimizer: ContextOptimizer;
  private vibecheckVerifier: VibecheckVerifier;
  private knowledge: CodebaseKnowledge | null = null;
  private statsPath: string;
  private stats: EngineStats;
  private lastContextUsed: ContextSource[] = [];

  constructor(workspaceRoot: string) {
    this.workspaceRoot = workspaceRoot;
    this.statsPath = path.join(workspaceRoot, '.guardrail', 'engine-stats.json');
    
    // Initialize all components
    this.verifier = new SemanticVerifier(workspaceRoot);
    this.detector = new HallucinationDetector(workspaceRoot);
    this.scorer = new ConfidenceScorer(workspaceRoot);
    this.learner = new PatternLearner(workspaceRoot);
    this.optimizer = new ContextOptimizer(workspaceRoot);
    this.vibecheckVerifier = new VibecheckVerifier(workspaceRoot);
    
    this.stats = this.loadStats();
  }

  /**
   * Analyze a code suggestion through all anti-hallucination layers
   */
  async analyze(suggestion: CodeSuggestion): Promise<AnalysisResult> {
    // Layer 1: Semantic Verification
    const verification = await this.verifier.verify(suggestion);
    
    // Layer 2: Hallucination Fingerprint Detection
    const detection = this.detector.detect(suggestion.code);
    
    // Layer 3: Confidence Scoring
    const confidence = this.scorer.score(suggestion, this.knowledge || undefined);
    
    // Layer 4: Pattern-based Scoring
    const patternScore = this.learner.scorePatterns(suggestion);

    // Combine all scores into final recommendation
    const summary = this.generateSummary(verification, detection, confidence, patternScore);
    
    // Update stats
    this.stats.totalAnalyzed++;
    if (summary.recommendation === 'accept') this.stats.accepted++;
    else if (summary.recommendation === 'reject') this.stats.rejected++;
    else this.stats.reviewed++;
    
    this.stats.averageConfidence = 
      (this.stats.averageConfidence * (this.stats.totalAnalyzed - 1) + summary.overallScore) / this.stats.totalAnalyzed;
    
    if (detection.detected) {
      this.stats.hallucinationsDetected++;
    }
    
    this.saveStats();

    return {
      suggestion,
      verification,
      detection,
      confidence,
      patternScore,
      shouldAccept: summary.recommendation === 'accept',
      shouldReview: summary.recommendation === 'review',
      shouldReject: summary.recommendation === 'reject',
      summary,
    };
  }

  /**
   * Quick check - fast path for obvious cases
   */
  quickCheck(code: string): { pass: boolean; issues: string[] } {
    const detection = this.detector.detect(code);
    
    if (detection.riskScore > 70) {
      return {
        pass: false,
        issues: detection.issues.slice(0, 3).map(i => i.message),
      };
    }

    return { pass: true, issues: [] };
  }

  /**
   * Record user feedback for learning
   */
  recordFeedback(feedback: LearningFeedback): void {
    // Feed to pattern learner
    this.learner.learn(feedback);
    
    // Feed to confidence scorer
    this.scorer.recordFeedback(
      { id: feedback.suggestionId, code: feedback.context, language: 'typescript', source: 'ai', timestamp: Date.now() },
      feedback.accepted,
      feedback.editedBefore,
      feedback.editDistance
    );

    // Update learning stats
    const learningStats = this.learner.getStats();
    this.stats.patternsLearned = learningStats.patternsLearned;
    this.stats.accuracyImprovement = learningStats.accuracyImprovement;
    this.saveStats();
  }

  /**
   * Get optimized context for a request and track it for Vibecheck badge
   */
  async getOptimizedContext(request: ContextRequest): Promise<OptimizedContext> {
    const context = await this.optimizer.optimize(request);
    
    // Track context for Vibecheck badge generation
    this.lastContextUsed = context.chunks.map(chunk => ({
      path: chunk.filePath,
      type: chunk.type === 'definition' || chunk.type === 'type' ? 'file' : 'file',
      relevance: chunk.relevanceScore,
      linesUsed: [{ start: chunk.startLine, end: chunk.endLine }],
    }));
    
    return context;
  }

  /**
   * Analyze and generate verified code with "Verified by Vibecheck" badge
   * This is the main method AI agents should use
   */
  async analyzeAndVerify(
    code: string,
    language: string,
    contextUsed?: ContextSource[]
  ): Promise<{
    analysis: AnalysisResult;
    verifiedCode: string;
    badge: VerificationBadge;
  }> {
    const suggestion: CodeSuggestion = {
      id: `suggestion-${Date.now()}`,
      code,
      language,
      source: 'ai',
      timestamp: Date.now(),
    };

    // Run full analysis
    const analysis = await this.analyze(suggestion);

    // Use provided context or last tracked context
    const context = contextUsed || this.lastContextUsed;

    // Generate Vibecheck badge
    const badge = this.vibecheckVerifier.generateBadge(
      code,
      analysis.summary.overallScore,
      analysis.summary.grade,
      context,
      analysis.summary.topIssues
    );

    // Add badge to code if verified
    const verifiedCode = this.vibecheckVerifier.addBadgeToCode(code, badge, language);

    return {
      analysis,
      verifiedCode,
      badge,
    };
  }

  /**
   * Generate a Vibecheck badge for already-analyzed code
   */
  generateVibecheckBadge(
    code: string,
    confidenceScore: number,
    confidenceGrade: string,
    contextUsed: ContextSource[],
    issues: string[] = []
  ): VerificationBadge {
    return this.vibecheckVerifier.generateBadge(
      code,
      confidenceScore,
      confidenceGrade,
      contextUsed,
      issues
    );
  }

  /**
   * Add Vibecheck badge to code
   */
  addVibecheckBadge(code: string, badge: VerificationBadge, language: string): string {
    return this.vibecheckVerifier.addBadgeToCode(code, badge, language);
  }

  /**
   * Check if code has a Vibecheck badge
   */
  hasVibecheckBadge(code: string): boolean {
    return this.vibecheckVerifier.hasVibecheckBadge(code);
  }

  /**
   * Verify the integrity of a Vibecheck badge in code
   */
  verifyVibecheckBadge(code: string): { valid: boolean; metadata?: any } {
    return this.vibecheckVerifier.verifyChecksum(code);
  }

  /**
   * Get Vibecheck verification statistics
   */
  getVibecheckStats(): {
    totalVerifications: number;
    verified: number;
    needsReview: number;
    avgConfidence: number;
    topContextSources: Array<{ path: string; count: number }>;
  } {
    return this.vibecheckVerifier.getStats();
  }

  /**
   * Update codebase knowledge (call after scanning)
   */
  updateKnowledge(knowledge: CodebaseKnowledge): void {
    this.knowledge = knowledge;
    this.verifier.updateKnowledge(knowledge);
    this.scorer.updateKnowledge(knowledge);
    this.optimizer.updateKnowledge(knowledge);
  }

  /**
   * Generate analysis summary
   */
  private generateSummary(
    verification: VerificationResult,
    detection: DetectionResult,
    confidence: ConfidenceScore,
    patternScore: { score: number; matchedPatterns: any[] }
  ): AnalysisSummary {
    // Calculate overall score (weighted combination)
    const weights = {
      verification: 0.30,
      detection: 0.25,
      confidence: 0.30,
      pattern: 0.15,
    };

    const verificationScore = verification.confidence;
    const detectionScore = 100 - detection.riskScore; // Invert risk score
    const confidenceScore = confidence.overall;
    const patternScoreValue = patternScore.score;

    const overallScore = Math.round(
      verificationScore * weights.verification +
      detectionScore * weights.detection +
      confidenceScore * weights.confidence +
      patternScoreValue * weights.pattern
    );

    // Determine grade
    let grade: string;
    if (overallScore >= 90) grade = 'A+';
    else if (overallScore >= 85) grade = 'A';
    else if (overallScore >= 80) grade = 'B+';
    else if (overallScore >= 75) grade = 'B';
    else if (overallScore >= 70) grade = 'C+';
    else if (overallScore >= 65) grade = 'C';
    else if (overallScore >= 60) grade = 'D';
    else grade = 'F';

    // Determine recommendation
    let recommendation: 'accept' | 'review' | 'reject';
    if (overallScore >= 80 && !verification.issues.some(i => i.severity === 'error')) {
      recommendation = 'accept';
    } else if (overallScore >= 60 || verification.issues.every(i => i.severity !== 'error')) {
      recommendation = 'review';
    } else {
      recommendation = 'reject';
    }

    // Hard reject conditions
    if (detection.riskScore > 80) {
      recommendation = 'reject';
    }
    if (verification.issues.filter(i => i.severity === 'error').length > 2) {
      recommendation = 'reject';
    }

    // Collect top issues
    const topIssues: string[] = [];
    
    // From verification
    verification.issues
      .filter(i => i.severity === 'error')
      .slice(0, 2)
      .forEach(i => topIssues.push(`❌ ${i.message}`));
    
    // From detection
    detection.issues
      .filter(i => i.severity === 'error')
      .slice(0, 2)
      .forEach(i => topIssues.push(`⚠️ ${i.message}`));
    
    // From confidence
    confidence.reasoning
      .filter(r => r.startsWith('⚠'))
      .slice(0, 2)
      .forEach(r => topIssues.push(r));

    // Collect top strengths
    const topStrengths: string[] = [];
    
    confidence.reasoning
      .filter(r => r.startsWith('✓'))
      .slice(0, 3)
      .forEach(r => topStrengths.push(r));
    
    if (verification.metrics.importVerification >= 90) {
      topStrengths.push('✓ All imports verified');
    }
    if (verification.metrics.apiVerification >= 90) {
      topStrengths.push('✓ API calls match signatures');
    }
    if (patternScore.score >= 70) {
      topStrengths.push('✓ Follows learned patterns');
    }

    // Determine risk level
    let riskLevel: 'low' | 'medium' | 'high' | 'critical';
    if (detection.riskScore > 70 || verification.issues.filter(i => i.severity === 'error').length > 3) {
      riskLevel = 'critical';
    } else if (detection.riskScore > 50 || verification.issues.filter(i => i.severity === 'error').length > 1) {
      riskLevel = 'high';
    } else if (detection.riskScore > 30 || verification.issues.some(i => i.severity === 'error')) {
      riskLevel = 'medium';
    } else {
      riskLevel = 'low';
    }

    return {
      overallScore,
      grade,
      recommendation,
      topIssues: topIssues.slice(0, 5),
      topStrengths: topStrengths.slice(0, 5),
      riskLevel,
    };
  }

  /**
   * Get engine statistics
   */
  getStats(): EngineStats {
    return { ...this.stats };
  }

  /**
   * Get detailed breakdown for dashboard
   */
  getDashboardData(): {
    stats: EngineStats;
    thresholds: { accept: number; review: number };
    recentHallucinations: { type: string; count: number }[];
    patternStats: LearningStats;
    confidenceHistory: { date: string; avgConfidence: number }[];
  } {
    const patternStats = this.learner.getStats();
    const thresholds = this.scorer.getThresholds();
    
    // Aggregate hallucination types
    const fingerprints = this.detector.getAllFingerprints();
    const hallucinationCounts = fingerprints
      .filter(f => f.frequency > 0)
      .reduce((acc, f) => {
        acc[f.type] = (acc[f.type] || 0) + Math.round(f.frequency * 100);
        return acc;
      }, {} as Record<string, number>);
    
    const recentHallucinations = Object.entries(hallucinationCounts)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      stats: this.stats,
      thresholds,
      recentHallucinations,
      patternStats,
      confidenceHistory: [], // Would need historical tracking
    };
  }

  /**
   * Get improvement suggestions
   */
  getImprovementSuggestions(): string[] {
    const suggestions: string[] = [];
    
    // Based on stats
    if (this.stats.hallucinationsDetected > this.stats.totalAnalyzed * 0.3) {
      suggestions.push('High hallucination rate detected - consider using more specific prompts');
    }
    
    if (this.stats.averageConfidence < 60) {
      suggestions.push('Low average confidence - AI may not be familiar with your codebase patterns');
    }

    // Based on pattern learning
    const negativePatterns = this.learner.getNegativePatterns();
    if (negativePatterns.length > 0) {
      const topNegative = negativePatterns[0];
      suggestions.push(`Frequently rejected pattern: ${topNegative.pattern} - add to custom rules`);
    }

    // Based on confidence thresholds
    const thresholds = this.scorer.getThresholds();
    if (thresholds.accept > 80) {
      suggestions.push('High acceptance threshold - the system has learned to be strict based on your feedback');
    }

    return suggestions;
  }

  /**
   * Export all learned data for backup
   */
  exportLearnedData(): string {
    return JSON.stringify({
      patterns: this.learner.exportPatterns(),
      stats: this.stats,
      exportedAt: new Date().toISOString(),
    }, null, 2);
  }

  /**
   * Reset learning (use with caution)
   */
  resetLearning(): void {
    this.stats = {
      totalAnalyzed: 0,
      accepted: 0,
      rejected: 0,
      reviewed: 0,
      averageConfidence: 0,
      hallucinationsDetected: 0,
      patternsLearned: 0,
      accuracyImprovement: 0,
    };
    this.saveStats();
    
    // Clear guardrail directory files
    const files = ['learned-patterns.json', 'confidence-history.json', 'hallucination-fingerprints.json'];
    for (const file of files) {
      const filePath = path.join(this.workspaceRoot, '.guardrail', file);
      if (fs.existsSync(filePath)) {
        fs.unlinkSync(filePath);
      }
    }
  }

  private loadStats(): EngineStats {
    try {
      if (fs.existsSync(this.statsPath)) {
        return JSON.parse(fs.readFileSync(this.statsPath, 'utf-8'));
      }
    } catch (e) {
      console.error('Failed to load engine stats:', e);
    }
    
    return {
      totalAnalyzed: 0,
      accepted: 0,
      rejected: 0,
      reviewed: 0,
      averageConfidence: 0,
      hallucinationsDetected: 0,
      patternsLearned: 0,
      accuracyImprovement: 0,
    };
  }

  private saveStats(): void {
    try {
      const dir = path.dirname(this.statsPath);
      if (!fs.existsSync(dir)) {
        fs.mkdirSync(dir, { recursive: true });
      }
      fs.writeFileSync(this.statsPath, JSON.stringify(this.stats, null, 2));
    } catch (e) {
      console.error('Failed to save engine stats:', e);
    }
  }
}

// Export a factory function for easy instantiation
export function createAntiHallucinationEngine(workspaceRoot: string): AntiHallucinationEngine {
  return new AntiHallucinationEngine(workspaceRoot);
}
